// UI sound, synthesised (SPEC §16).
//
// "UI sounds are synthesised procedurally in Web Audio — oscillators, filtered
// noise, envelopes. Clicks, reticle locks, panel opens, radar pings,
// budget-replenish chirps, countdown ticks. Zero asset weight, infinite variation
// without audible repetition, parameterisable by event weight, and exactly the
// right texture for an instrument-panel interface (§15). Hand-authored samples
// would sound *worse* here, so this is not a fallback — it is the better answer."
//
// Every voice below is one function that builds a small graph, schedules an
// envelope against `ctx.currentTime`, and returns. Nothing is retained, nothing
// is pooled, and no voice outlives its own envelope: an oscillator that has been
// `stop()`ed is garbage, and a synth that keeps a node pool to avoid allocating
// three objects is optimising the wrong thing while holding a real risk of a
// stuck note.
//
// The parameterisation is the whole point. §16 asks for "parameterisable by event
// weight": a coalesced four-click attack is not four clicks played four times,
// it is one heavier click. That is one multiply here and it is impossible with a
// sample bank.

import type { AudioNodeLike, SynthContext } from './graph';

/** The interface's own sounds. Impacts and voice lines are assets, and are not here. */
export type UISound =
  | 'click'
  | 'reticle'
  | 'panel'
  | 'ping'
  | 'refill'
  | 'countdown'
  | 'refuse'
  | 'capture'
  | 'lost';

export interface VoiceOptions {
  /**
   * Event weight, 0..1. A coalesced attack, a bigger capture, a louder alert.
   * Drives pitch, length and gain together, because changing only one of the
   * three reads as the same sound played wrong rather than as a bigger event.
   */
  weight?: number;
  /** Extra gain trim for the caller's bus, 0..1. */
  gain?: number;
  /** Scheduling offset in seconds, for a burst that should not phase-cancel itself. */
  delay?: number;
}

interface Shape {
  /** Oscillator waveform. */
  type: string;
  /** Start and end frequency in Hz. A fall reads as a confirmation, a rise as a question. */
  from: number;
  to: number;
  /** Seconds. */
  attack: number;
  decay: number;
  /** Peak gain before the caller's trim. */
  peak: number;
  /** A band-passed noise layer, for the sounds that need air rather than tone. */
  noise?: { hz: number; q: number; peak: number; decay: number };
}

/**
 * The voice table.
 *
 * Written as data rather than as nine functions so the whole palette is legible
 * at once — which matters because these have to sound like one instrument, and
 * nine hand-rolled graphs drift apart the moment two people touch them.
 */
const SHAPES: Record<UISound, Shape> = {
  // The core verb. Short, dry, dropping — a switch being thrown, not a beep.
  click: { type: 'square', from: 880, to: 620, attack: 0.001, decay: 0.05, peak: 0.22 },
  // The reticle finding a pixel. Barely there; it fires on every mouse move.
  reticle: { type: 'sine', from: 1650, to: 1650, attack: 0.001, decay: 0.02, peak: 0.05 },
  // A panel opening. Two-stage, upward, with a breath of filtered noise.
  panel: {
    type: 'triangle',
    from: 320,
    to: 520,
    attack: 0.004,
    decay: 0.12,
    peak: 0.1,
    noise: { hz: 2200, q: 0.8, peak: 0.05, decay: 0.09 },
  },
  // The radar ping. Long tail, high Q — the only sound here allowed to ring.
  ping: { type: 'sine', from: 1320, to: 1180, attack: 0.002, decay: 0.42, peak: 0.11 },
  // A click landing back in the bank (§14). Rising, so it reads as a gain.
  refill: { type: 'triangle', from: 640, to: 960, attack: 0.002, decay: 0.09, peak: 0.14 },
  // The countdown tick. Flat, mechanical, identical every time on purpose.
  countdown: { type: 'square', from: 1200, to: 1200, attack: 0.001, decay: 0.03, peak: 0.09 },
  // Refused: illegal, no budget, frozen. A hard low buzz — it must not be
  // mistakable for a click that landed.
  refuse: { type: 'sawtooth', from: 190, to: 120, attack: 0.001, decay: 0.14, peak: 0.16 },
  // The killing blow. The one UI sound with any bloom to it.
  capture: {
    type: 'triangle',
    from: 520,
    to: 1040,
    attack: 0.002,
    decay: 0.3,
    peak: 0.2,
    noise: { hz: 3200, q: 1.2, peak: 0.09, decay: 0.22 },
  },
  // Someone beat you to it. Falls, and falls further than a click.
  lost: { type: 'sine', from: 700, to: 330, attack: 0.002, decay: 0.22, peak: 0.14 },
};

export function shapeOf(sound: UISound): Shape {
  return SHAPES[sound];
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Builds and schedules one UI voice.
 *
 * Returns the gain node it built, so a caller can inspect or disconnect it. The
 * graph is always `osc -> gain -> out`, plus `noise -> filter -> noiseGain -> out`
 * for the two shapes that carry air.
 */
export function playUI(
  ctx: SynthContext,
  out: AudioNodeLike,
  sound: UISound,
  opts: VoiceOptions = {},
): AudioNodeLike {
  const shape = SHAPES[sound];
  const weight = clamp01(opts.weight ?? 0);
  const trim = clamp01(opts.gain ?? 1);
  const t0 = ctx.currentTime + Math.max(0, opts.delay ?? 0);

  // Weight moves all three at once: heavier events are lower, longer and louder,
  // which is how a real mechanism behaves and how a synthetic one avoids sounding
  // like the same beep at a different volume.
  const detune = 1 - 0.28 * weight;
  const from = shape.from * detune;
  const to = shape.to * detune;
  const decay = shape.decay * (1 + 0.55 * weight);
  const peak = Math.min(1, shape.peak * (0.75 + 0.5 * weight)) * trim;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + shape.attack);
  // Exponential release: linear decay to zero is audible as a click at the end of
  // every voice, which on a sound that fires on every player action is the worst
  // possible place for it.
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + shape.attack + decay);
  gain.connect(out);

  const osc = ctx.createOscillator();
  osc.type = shape.type;
  osc.frequency.setValueAtTime(from, t0);
  if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t0 + shape.attack + decay);
  osc.connect(gain);
  osc.start(t0);
  osc.stop(t0 + shape.attack + decay + 0.02);

  if (shape.noise) {
    const n = shape.noise;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, n.decay + 0.02);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(n.hz * detune, t0);
    filter.Q.setValueAtTime(n.q, t0);
    const ngain = ctx.createGain();
    ngain.gain.setValueAtTime(0.0001, t0);
    ngain.gain.linearRampToValueAtTime(n.peak * trim * (0.7 + 0.6 * weight), t0 + 0.004);
    ngain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.decay * (1 + 0.5 * weight));
    src.connect(filter);
    filter.connect(ngain);
    ngain.connect(out);
    src.start(t0);
    src.stop(t0 + n.decay + 0.05);
  }

  return gain;
}

/**
 * An impact, synthesised.
 *
 * §16 puts the heavy impacts in the `mediagen` sfx bank as generated assets, and
 * that is still where they belong — this is the layer that plays while the bank
 * is absent, so a shot landing is never silent in a build that has no audio
 * assets in it. It is deliberately plain: filtered noise with a pitch-swept body,
 * scaled by weight, and it does not try to be the bespoke siege set.
 */
export function playImpact(
  ctx: SynthContext,
  out: AudioNodeLike,
  weight: number,
  kill = false,
  trim = 1,
): AudioNodeLike {
  const w = clamp01(weight);
  const t0 = ctx.currentTime;
  const decay = 0.12 + 0.28 * w + (kill ? 0.14 : 0);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.linearRampToValueAtTime((0.1 + 0.22 * w + (kill ? 0.1 : 0)) * clamp01(trim), t0 + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
  gain.connect(out);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  // A heavier hit opens the filter: that, not volume, is what makes one impact
  // sound bigger than another.
  filter.frequency.setValueAtTime(420 + 2600 * w + (kill ? 900 : 0), t0);
  filter.frequency.exponentialRampToValueAtTime(180, t0 + decay);
  filter.Q.setValueAtTime(0.9, t0);
  filter.connect(gain);

  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, decay + 0.05);
  src.connect(filter);
  src.start(t0);
  src.stop(t0 + decay + 0.05);

  const body = ctx.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(160 - 60 * w, t0);
  body.frequency.exponentialRampToValueAtTime(46, t0 + decay);
  body.connect(gain);
  body.start(t0);
  body.stop(t0 + decay + 0.02);

  return gain;
}

/**
 * White noise, built once per call.
 *
 * Short buffers only — the longest thing here is under half a second — so this is
 * a few thousand samples and not worth a cache that would then have to be
 * invalidated when the context changes.
 */
export function noiseBuffer(ctx: SynthContext, seconds: number): AudioBufferLikeShim {
  const n = Math.max(1, Math.floor(ctx.sampleRate * Math.max(0.001, seconds)));
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

type AudioBufferLikeShim = ReturnType<SynthContext['createBuffer']>;
